import { useState } from 'react'
import { Handle, Position, useReactFlow } from '@xyflow/react'
import { Image, Zap, Loader, X, Copy, Download, Lock, Unlock } from 'lucide-react'
import useStore from '../../store/useStore'
import { buildFluxWorkflow, buildFluxSchnellWorkflow, estimateCost, submitJob, pollJob, parseImageOutput } from '../../api/runpod'
import CostConfirmDialog from '../ui/CostConfirmDialog'

const MODELS    = ['FLUX.1 Dev', 'FLUX.1 Schnell']
const STYLES    = ['Claymation', 'Animated', 'Realistic', 'Illustrated', 'Cinematic']
const RATIOS    = [
  { label: '9:16', w: 576,  h: 1024, icon: '▯' },
  { label: '1:1',  w: 1024, h: 1024, icon: '□' },
  { label: '16:9', w: 1024, h: 576,  icon: '▭' },
  { label: '4:5',  w: 819,  h: 1024, icon: '▯' },
  { label: '3:4',  w: 768,  h: 1024, icon: '▯' },
]
const QUANTITIES = [1, 2, 4, 6]

export default function ImageGeneratorNode({ id, data, selected }) {
  const { deleteElements, addNodes, getNode } = useReactFlow()
  const { characters, addJob, updateJob, completeJob, failJob, addImage, addNotification } = useStore()

  const [prompt, setPrompt]               = useState('')
  const [negative, setNegative]           = useState('')
  const [selectedModel, setSelectedModel] = useState('FLUX.1 Dev')
  const [selectedStyle, setSelectedStyle] = useState('Claymation')
  const [selectedCharacter, setSelectedCharacter] = useState('')
  const [selectedRatio, setSelectedRatio] = useState(RATIOS[0])
  const [quantity, setQuantity]           = useState(1)
  const [seed, setSeed]                   = useState('')
  const [seedLocked, setSeedLocked]       = useState(false)
  const [generating, setGenerating]       = useState(false)
  const [results, setResults]             = useState([])
  const [selectedImg, setSelectedImg]     = useState(null)
  const [showCostDialog, setShowCostDialog] = useState(false)

  const character = characters.find(c => c.id === selectedCharacter)
  const cost      = estimateCost('flux_image', { quantity })

  const handleDelete    = () => deleteElements({ nodes: [{ id }] })
  const handleDuplicate = () => {
    const node = getNode(id)
    if (!node) return
    addNodes({
      ...node,
      id: `imageGenerator_${Date.now()}`,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: false,
    })
  }

  const doGenerate = async () => {
    const fullPrompt = [
      character?.trigger_word,
      selectedStyle.toLowerCase(),
      prompt.trim(),
    ].filter(Boolean).join(', ')

    const jobId = `img_${Date.now()}`
    setGenerating(true)
    setResults([])
    addJob({ id: jobId, label: `${quantity} × ${selectedModel}`, type: 'flux_image', status: 'IN_QUEUE' })

    try {
      // Build real ComfyUI workflow — generate each image sequentially for quantity > 1
      // (batch_size > 1 requires more VRAM; sequential is safer on first run)
      const allImages = []

      for (let i = 0; i < quantity; i++) {
        const thisSeed = seedLocked && seed ? parseInt(seed) + i : undefined

        const workflow = selectedModel === 'FLUX.1 Schnell'
          ? buildFluxSchnellWorkflow({
              prompt: fullPrompt,
              width:  selectedRatio.w,
              height: selectedRatio.h,
              seed:   thisSeed,
            })
          : buildFluxWorkflow({
              prompt:    fullPrompt,
              width:     selectedRatio.w,
              height:    selectedRatio.h,
              steps:     selectedModel === 'FLUX.1 Dev' ? 20 : 4,
              guidance:  3.5,
              seed:      thisSeed,
            })

        updateJob(jobId, { status: `IN_PROGRESS (${i + 1}/${quantity})` })
        const { id: runpodJobId } = await submitJob(workflow)
        const output = await pollJob(runpodJobId, (status) => updateJob(jobId, { status }))

        // Parse base64 images from ComfyUI worker output
        const dataUrls = parseImageOutput(output)
        allImages.push(...dataUrls)
      }

      const imageObjects = allImages.map((dataUrl, i) => ({
        id: `img_${Date.now()}_${i}`,
        url: dataUrl,
        prompt: fullPrompt,
        model: selectedModel,
        character: character?.name || 'None',
        ratio: selectedRatio.label,
        createdAt: Date.now(),
      }))

      imageObjects.forEach(img => addImage(img))
      setResults(imageObjects)
      completeJob(jobId, { output: { images: allImages } })
      addNotification({ type: 'success', message: `Generated ${allImages.length} image(s).` })
    } catch (err) {
      failJob(jobId, err.message)
      addNotification({ type: 'error', message: `Failed: ${err.message}` })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className={`aiq-node ${selected ? 'selected' : ''}`} style={{ minWidth: 290 }}>

      {/* Handles */}
      <Handle type="target" position={Position.Left}  id="ref-in"    style={{ top: '30%' }} />
      <Handle type="source" position={Position.Right} id="image-out" style={{ top: '50%' }} />

      {/* Header */}
      <div className="aiq-node-header">
        <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
          <Image size={14} className="text-blue-400" />
        </div>
        <span className="text-sm font-semibold text-white flex-1">Image Generator</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-mono">
          {selectedModel === 'FLUX.1 Dev' ? 'DEV' : 'SCH'}
        </span>
        <button onClick={handleDuplicate} className="ml-1 p-1 rounded hover:bg-surface-hover text-slate-500 hover:text-slate-300 transition-colors" title="Duplicate">
          <Copy size={12} />
        </button>
        <button onClick={handleDelete} className="p-1 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors" title="Delete">
          <X size={13} />
        </button>
      </div>

      {/* Body */}
      <div className="aiq-node-body">

        {/* Character */}
        {characters.length > 0 && (
          <div>
            <label className="label">Character</label>
            <select value={selectedCharacter} onChange={e => setSelectedCharacter(e.target.value)} className="input text-xs">
              <option value="">None</option>
              {characters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {/* Model */}
        <div>
          <label className="label">Model</label>
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="input text-xs">
            {MODELS.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        {/* Style */}
        <div>
          <label className="label">Style</label>
          <div className="flex flex-wrap gap-1">
            {STYLES.map(s => (
              <button key={s} onClick={() => setSelectedStyle(s)}
                className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                  selectedStyle === s
                    ? 'bg-brand-500 border-brand-400 text-white'
                    : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/40'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div>
          <label className="label">Aspect Ratio</label>
          <div className="flex gap-1">
            {RATIOS.map(r => (
              <button key={r.label} onClick={() => setSelectedRatio(r)}
                className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors ${
                  selectedRatio.label === r.label
                    ? 'bg-brand-500 border-brand-400 text-white'
                    : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/40'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Prompt */}
        <div>
          <label className="label">Scene Description</label>
          <textarea rows={3} className="input text-xs resize-none"
            placeholder="Character at a whiteboard, confident expression, bright studio lighting..."
            value={prompt} onChange={e => setPrompt(e.target.value)} />
        </div>

        {/* Negative */}
        <div>
          <label className="label">Negative Prompt</label>
          <input type="text" className="input text-xs"
            placeholder="blurry, low quality..."
            value={negative} onChange={e => setNegative(e.target.value)} />
        </div>

        {/* Quantity + Seed row */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="label">Qty</label>
            <div className="flex gap-1">
              {QUANTITIES.map(q => (
                <button key={q} onClick={() => setQuantity(q)}
                  className={`flex-1 py-1 text-xs rounded-lg border transition-colors ${
                    quantity === q
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/40'
                  }`}>
                  {q}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <label className="label flex items-center gap-1">
              Seed
              <button onClick={() => setSeedLocked(!seedLocked)}
                className={`transition-colors ${seedLocked ? 'text-brand-400' : 'text-slate-600 hover:text-slate-400'}`}
                title={seedLocked ? 'Seed locked' : 'Seed unlocked'}>
                {seedLocked ? <Lock size={10} /> : <Unlock size={10} />}
              </button>
            </label>
            <input type="number" className="input text-xs"
              placeholder="random"
              value={seed} onChange={e => setSeed(e.target.value)}
              disabled={!seedLocked} />
          </div>
        </div>

        {/* Cost */}
        {cost && (
          <div className="text-xs text-green-400">Est: {cost.estimatedCost} ({quantity} × {cost.costPerUnit})</div>
        )}

        {/* Generate */}
        <button
          onClick={() => setShowCostDialog(true)}
          disabled={generating || !prompt.trim()}
          className="btn-primary w-full justify-center"
        >
          {generating
            ? <><Loader size={14} className="generation-spinner" /> Generating...</>
            : <><Zap size={14} /> Generate</>}
        </button>

        {/* Results grid */}
        {results.length > 0 && (
          <div>
            <label className="label">Output ({results.length})</label>
            <div className="grid grid-cols-2 gap-1.5">
              {results.map((img, i) => (
                <div key={img.id} className="relative group rounded-lg overflow-hidden border border-surface-border">
                  {img.url
                    ? <img src={img.url} alt={`Result ${i+1}`} className="w-full aspect-square object-cover" />
                    : <div className="w-full aspect-square bg-surface-muted flex items-center justify-center">
                        <Image size={20} className="text-slate-600" />
                      </div>
                  }
                  {/* Hover actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <a href={img.url} download className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white">
                      <Download size={13} />
                    </a>
                    <button
                      onClick={() => navigator.clipboard.writeText(prompt)}
                      className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white"
                      title="Copy prompt">
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cost confirm dialog */}
      {showCostDialog && (
        <CostConfirmDialog
          jobLabel={`${quantity} image${quantity > 1 ? 's' : ''} · ${selectedModel} · ${selectedRatio.label}`}
          estimatedCost={cost?.estimatedCost || '~$0.003'}
          onConfirm={() => { setShowCostDialog(false); doGenerate() }}
          onCancel={() => setShowCostDialog(false)}
        />
      )}
    </div>
  )
}
